import { Box, CircularProgress, TextField, Typography, styled, useTheme } from '@mui/material';
import { MUIDataTableColumnDef } from 'mui-datatables';
import { UserTrainingStatistics } from 'models/UserTrainingStatistics';
import MUIDataTable from 'mui-datatables';
import { RewardType } from 'models/RewardType';
import CloseIcon from '@mui/icons-material/Close';
import { StyledTableContainer, StyledLoadingContainer, StyledRewardContainer, StyledStarIcon, StyledTableHeader } from 'components/StatisticsDataTable/StatisticsDataTable.styles';
import { LessonProgressStatus } from 'models/LessonProgressStatus';
import { useTranslation } from 'react-i18next';
import { convertToStatisticDataTableDto } from 'components/StatisticsDataTable/Utils';
import { useRewardTypeDictionaries } from 'components/StatisticsDataTable/hooks/useRewardTypeDictionaries';
import { DEFAULT_SCORE, getScoreFromCompareArg, getScoreFromRowData, isScore, makeScoreFilterLogic, makeSortScore} from 'components/StatisticsDataTable/Columns/ScoreColumn';
import { buildMinutesPredicate } from 'components/StatisticsDataTable/Columns/TimeSpentColumn';
import { DataTableStatisticDto, Hms } from 'components/StatisticsDataTable/Dto/DataTableStatisticDto';
import { OverridenGlobalStyles } from 'components/StatisticsDataTable/OverridenGlobalStyles';
import { MakeSortableHead } from 'helpers/sortTableHeaderHelper';
import { buildStatisticsCsv, generateFileName } from 'components/StatisticsDataTable/csvUtils';
import { CustomStatisticsToolbar } from './CustomStatisticsToolbar';

interface UserTrainingStatisticsTableProps {
  data: UserTrainingStatistics[];
  gettingStatistics: boolean;
}

const StyledMUIDataTable = styled(MUIDataTable)`
  & .tss-1cdcmys-MUIDataTable-responsiveBase {
    overflow: visible !important;
  }
  & .MuiTableCell-head span button {
    font-size: 16px !important;
  }
`;

const StyledFilterBox = styled(Box)(({ theme }) => ({
  width: '100%',
  paddingTop: theme.spacing(2),
}));

export const UserTrainingStatisticsTable = (props: UserTrainingStatisticsTableProps) => {
  const { data, gettingStatistics } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { rewardTypeDictionary, revertedRewardDictionary } = useRewardTypeDictionaries();

  const adjustedData: DataTableStatisticDto[] = convertToStatisticDataTableDto(data, rewardTypeDictionary);

  const getRewardIconColor = (rewardType: RewardType) => {
    switch (rewardType) {
      case RewardType.Gold:
        return 'yellow';
      case RewardType.Silver:
        return 'silver';
      case RewardType.Bronze:
        return 'brown';
      default:
        return 'none';
    }
  };

  const getLessonProgressStatusText = (status: LessonProgressStatus) => {
    switch (status) {
      case LessonProgressStatus.NotStarted:
        return t('statistics.lessonProgressStatus.notStarted');
      case LessonProgressStatus.InProgress:
        return t('statistics.lessonProgressStatus.inProgress');
      case LessonProgressStatus.Canceled:
        return t('statistics.lessonProgressStatus.canceled');
      default:
        return '';
    }
  };

   const scoreFilters = {
    noPoints: t('statistics.filters.noPoints'),
    below50: t('statistics.filters.below50'),
    above50: t('statistics.filters.above50'),
    max: t('statistics.filters.max'),
  };

  const columns = [
    {
      name: 'userDisplayName',
      label: t('statistics.userName'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: string) => (
          <Typography textAlign={'left'} ml="0.4rem">{value}</Typography>
        ),
        customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
          return (
              <StyledTableHeader key={columnMeta.index} $textAlign={'left'} onClick={() => _handleToggleColumn(columnMeta.index)}>
                <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                  <Typography paddingLeft={theme.spacing(1.2)} textAlign={'left'} fontWeight={'500'}>{columnMeta.label}</Typography>
                </MakeSortableHead>
              </StyledTableHeader>
          );
        },
        setCellProps: () => ({  
          style: { paddingLeft: 0 }
        })
      },
    },
    {
      name: 'lessonName',
      label: t('statistics.lessonName'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: string) => (
          <Typography ml="0.4rem" textAlign={'center'}>
                {value}
            </Typography>
        ),
        customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
          return (
              <StyledTableHeader key={columnMeta.index} $textAlign={'center'} onClick={() => _handleToggleColumn(columnMeta.index)}>
                <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                  <Typography textAlign={'center'} fontWeight={'500'}>{columnMeta.label}</Typography>
                </MakeSortableHead>
              </StyledTableHeader>
          );
        },
      },
    },
    {
      name: 'userTagA',
      label: t('statistics.tagA'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: string) => (
          <Typography textAlign={'center'}>{value}</Typography>
        ),
        customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
          return (
              <StyledTableHeader key={columnMeta.index} $textAlign={'center'} onClick={() => _handleToggleColumn(columnMeta.index)}>
                <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                  <Typography textAlign={'center'} fontWeight={'500'}>{columnMeta.label}</Typography>
                </MakeSortableHead>
              </StyledTableHeader>
          );
        },
      },
    },
    {
      name: 'userTagB',
      label: t('statistics.tagB'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: string) => (
          <Typography textAlign={'center'}>{value}</Typography>
        ),
        customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
         return (
             <StyledTableHeader key={columnMeta.index} $textAlign={'center'} onClick={() => _handleToggleColumn(columnMeta.index)}>
               <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                 <Typography textAlign={'center'} fontWeight={'500'}>{columnMeta.label}</Typography>
               </MakeSortableHead>
             </StyledTableHeader>
         );
        },
      },
    },
    {
      name: 'dateCompleted',
      label: t('statistics.dateCompleted'),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: string | null, tableMeta: any) => {
          const status = data[tableMeta.rowIndex].lessonProgressStatus;
          if (status !== LessonProgressStatus.Finished) {
            value = getLessonProgressStatusText(status);
          }
          return <Typography textAlign={'center'}>{value}</Typography>
        },
        customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
         return (
             <StyledTableHeader key={columnMeta.index} $textAlign={'center'} onClick={() => _handleToggleColumn(columnMeta.index)}>
               <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                 <Typography textAlign={'center'} fontWeight={'500'}>{columnMeta.label}</Typography>
               </MakeSortableHead>
             </StyledTableHeader>
         );
        },
      }
    },
    {
      name: 'rewardType',
      label: t('statistics.rewardType'),
      options: {
        filter: true,
        sort: true,
        customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
         return (
             <StyledTableHeader key={columnMeta.index} $textAlign={'center'} onClick={() => _handleToggleColumn(columnMeta.index)}>
               <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                 <Typography textAlign={'center'} fontWeight={'500'}>{columnMeta.label}</Typography>
               </MakeSortableHead>
             </StyledTableHeader>
         );
        },
        customBodyRender: (value: string) =>
          {
            const noRewardValue = t('rewardType.none');
            const color = revertedRewardDictionary[value];
  
            return  (
            <StyledRewardContainer>
              {value === noRewardValue
              ? <CloseIcon />
              : <StyledStarIcon $color={getRewardIconColor(color)}/>}
            </StyledRewardContainer>
          )
          }
      },
    },
    {
      name: 'timeSpent',
      label: t('statistics.timeSpent'),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: string | null) => (
          <Typography textAlign={'center'}>{value == null ? '' :  value}</Typography>
        ),
        customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
         return (
             <StyledTableHeader key={columnMeta.index} $textAlign={'center'} onClick={() => _handleToggleColumn(columnMeta.index)}>
               <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                 <Typography textAlign={'center'} fontWeight={'500'}>{columnMeta.label}</Typography>
               </MakeSortableHead>
             </StyledTableHeader>
         );
        },
      }
    },
    {
      name: 'convertedTimeSpent',
      label: 'Time spent (min)',
      options: {
        display: 'excluded',
        sort: false,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (v: unknown) => {
            const first = Array.isArray(v) ? v[0] : v;
            const s = String(first ?? '').trim();
            return s ? `${t('statistics.timeSpent')}: ${s}` : '';
          },
        },
        filterOptions: {
          logic: (cellValue: Hms | null, filters: unknown[]) => {
            const raw = (filters?.[0] as string) ?? '';
            const pred = buildMinutesPredicate(raw);
            if (!pred) return false;
            return !pred(cellValue);
          },
          display: (filterList, onChange, index, column) => {
            const val: string = (filterList[index]?.[0] as string) ?? '';
            const normalized = val.replace(/\s+/g, '');
            const isValid = val.trim() === '' || /^[<>]\d+(?:\.\d+)?$/.test(normalized);

            const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
              const next = e.target.value;
              const nextFilterList = [...filterList];
              nextFilterList[index] = [next];
              onChange(nextFilterList[index], index, column);
            };

        return (
          <StyledFilterBox>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={t('statistics.filterMinutesLabel')}
              placeholder="> 10 / < 10"
              value={val}
              onChange={handleChange}
              error={val.trim() !== '' && !isValid}
              helperText={t('statistics.filterMinutesHelp')}
              InputProps={{ inputProps: { inputMode: 'text' } }}
            />
          </StyledFilterBox>
          );
          },
        },
      },
    },
    {
      name: 'score',
      label: t('statistics.points'),
      options: {
        filter: true,
        filterType: "dropdown",
        filterOptions: {
          names: [
            scoreFilters.noPoints,
            scoreFilters.below50,
            scoreFilters.above50,
            scoreFilters.max
          ],
          logic: makeScoreFilterLogic(scoreFilters, getScoreFromRowData),
          customFilterListOptions: {
            render: (v: string[]) => v,
          },
        },
       sortCompare: makeSortScore(getScoreFromCompareArg),
       customBodyRender: (value: any, _) => {
        const s = isScore(value) ? value : DEFAULT_SCORE;
        return <Typography textAlign={'center'}>{`${s.scoredPoints}/${s.maxPoints}`}</Typography>;
       },
       customHeadRender: ( columnMeta, _handleToggleColumn, sortOrder) => {
         return (
             <StyledTableHeader key={columnMeta.index} $textAlign={'center'} onClick={() => _handleToggleColumn(columnMeta.index)}>
               <MakeSortableHead columnMeta={columnMeta} sortOrder={sortOrder}>
                 <Typography textAlign={'center'} fontWeight={'500'}>{columnMeta.label}</Typography>
               </MakeSortableHead>
             </StyledTableHeader>
         );
        },
      },
    },
  ] as MUIDataTableColumnDef[];

  return (
    <StyledTableContainer>
      {gettingStatistics ? (
        <StyledLoadingContainer>
          <CircularProgress />
        </StyledLoadingContainer>
      ) : (
        <OverridenGlobalStyles>
          <StyledMUIDataTable title={t('statistics.usersResult')} data={adjustedData} columns={columns} options={{
            textLabels: {
              body: {
                noMatch: t('dataTable.noMatch'),
                toolTip: t('dataTable.toolTip'),
                columnHeaderTooltip: column => t('dataTable.columnHeaderTooltip', { column: column.label })
              },
              pagination: {
                next: t('dataTable.next'),
                previous: t('dataTable.previous'),
                rowsPerPage: t('dataTable.rowsPerPage'),
                displayRows: t('dataTable.displayRows'),
              },
              toolbar: {
                search: t('dataTable.search'),
                downloadCsv: t('dataTable.downloadCsv'),
                print: t('dataTable.print'),
                viewColumns: t('dataTable.viewColumns'),
                filterTable: t('dataTable.filterTable'),
              },
              filter: {
                all: t('dataTable.all'),
                title: t('dataTable.title'),
                reset: t('dataTable.reset'),
              },
              viewColumns: {
                title: t('dataTable.viewColumnsTitle'),
                titleAria: t('dataTable.viewColumnsTitleAria'),
              },
              selectedRows: {
                text: t('dataTable.selectedRowsText'),
                delete: t('dataTable.delete'),
                deleteAria: t('dataTable.deleteAria'),
              },
          },
          fixedHeader: false,
          download: true,
          downloadOptions: {
          filename: generateFileName(),
            separator: ';',
            filterOptions: {
              useDisplayedColumnsOnly: true,
              useDisplayedRowsOnly: true
            }
          },
          onDownload: (_bh, _bb, cols, rows) => buildStatisticsCsv(t, cols, rows, { idxCompleted: 4, idxPoints: 7 }),
          customToolbarSelect: (selectedRows, displayData, setSelectedRows) => (
            <CustomStatisticsToolbar
            selectedRows={selectedRows}
            displayData={displayData}
            adjustedData={adjustedData}
            columns={columns}
            t={t}
            buildStatisticsCsv={buildStatisticsCsv}
            generateFileName={generateFileName}
            meta={{ idxCompleted: 4, idxPoints: 7 }}
            />
          ),
          onRowsDelete: () => false,      
          }}/>
        </OverridenGlobalStyles>
      )}
    </StyledTableContainer>
  );
};